'use client'

import CloseIcon from '@/app/icons/close'
import FavoriteIcon from '@/app/icons/favorite'
import PlayIcon from '@/app/icons/play'
import ShareIcon from '@/app/icons/share'
import { cn } from '@/util/cn'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
    EmbedGif as EmbedGifType,
    appendTrackingQuery,
    getDisplayName,
    getEmbedCode,
    getExternalMediaUrl,
    getGifImageUrl,
    getGifStillUrl,
    getGifVideoUrl,
    getGifWebpUrl,
    getSocialUrls,
    getUserUrl,
} from '../helpers'
import { GiphyWordmark } from './giphy-logo'

type Props = {
    children?: ReactNode
    gif: EmbedGifType
    hideVideo?: boolean
    isTwitter?: boolean
    trackingQueryString?: string
}

const iconButtonClassName =
    'flex size-10 items-center justify-center bg-giphyBlack/90 text-giphyWhite transition hover:bg-giphyCharcoal focus:outline-hidden focus:ring-2 focus:ring-giphyBlue'
const smallEmbedHiddenClassName = 'max-[250px]:hidden [@media(max-height:199px)]:hidden'

type ClipboardWriter = {
    writeText: (value: string) => Promise<void>
}

export async function writeEmbedClipboardText(
    value: string,
    clipboard: ClipboardWriter | undefined = typeof navigator === 'undefined' ? undefined : navigator.clipboard
) {
    if (!clipboard) {
        return false
    }

    try {
        await clipboard.writeText(value)
        return true
    } catch {
        return false
    }
}

function GifAttribution({ gif, trackingQueryString }: { gif: EmbedGifType; trackingQueryString?: string }) {
    const user = gif.user
    const displayName = getDisplayName(user)
    const userUrl = appendTrackingQuery(getUserUrl(user), trackingQueryString)

    if (!user || !user.is_public || user.suppress_chrome || !displayName || !userUrl) {
        return <div className="min-w-0 flex-1" />
    }

    return (
        <a
            href={userUrl}
            target="_blank"
            rel="noreferrer"
            className="flex h-10 min-w-0 flex-1 items-center gap-2 text-giphyWhite no-underline"
        >
            {user.avatar_url && (
                <img
                    src={user.avatar_url}
                    alt=""
                    className="size-10 shrink-0 rounded-sm bg-giphyDarkGrey object-cover"
                />
            )}
            <span className="max-w-[140px] min-w-0 text-left leading-[18px] whitespace-nowrap">
                <span className="block truncate text-sm font-black">{displayName}</span>
                {user.username && (
                    <span className="inline-flex max-w-full items-start gap-0.5 align-top text-sm font-bold">
                        <span className="truncate underline underline-offset-2">@{user.username.toLowerCase()}</span>
                        {user.is_verified && (
                            <img
                                src="/static/img/embeds/verified-badge.svg"
                                alt=""
                                className="h-[18px] w-[14px] shrink-0 pl-0.5"
                            />
                        )}
                    </span>
                )}
            </span>
        </a>
    )
}

function CopyField({ label, value, copyLabel }: { label: string; value: string; copyLabel: string }) {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const timeoutRef = useRef<number | null>(null)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const copyValue = async () => {
        inputRef.current?.select()

        const didCopy = await writeEmbedClipboardText(value)

        if (didCopy) {
            setCopied(true)
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current)
            }
            timeoutRef.current = window.setTimeout(() => setCopied(false), 1500)
        }
    }

    return (
        <div>
            <label className="block pb-1.5 text-sm font-black text-giphyLightGrey">{label}</label>
            <div className="relative flex h-12 w-full rounded-md bg-giphyWhite focus-within:ring-2 focus-within:ring-giphyBlue">
                {copied && (
                    <div
                        role="status"
                        className="pointer-events-none absolute -top-2 right-3 z-10 -translate-y-full rounded bg-giphyBlack px-2.5 py-1 text-xs font-black text-giphyWhite shadow-lg"
                    >
                        Copied to clipboard
                    </div>
                )}
                <input
                    ref={inputRef}
                    className="min-w-0 flex-1 rounded-l-md bg-giphyWhite px-3 text-base text-giphyBlack outline-hidden"
                    readOnly
                    spellCheck={false}
                    value={value}
                    onFocus={(event) => event.currentTarget.select()}
                    onClick={() => void copyValue()}
                />
                <button
                    type="button"
                    className="min-w-[112px] shrink-0 rounded-r-md bg-linear-to-r from-giphyPurple to-giphyIndigo px-4 text-sm font-black text-giphyWhite transition hover:from-giphyPink hover:to-giphyPurple focus:outline-hidden md:min-w-[132px] md:text-base"
                    onClick={() => void copyValue()}
                >
                    {copyLabel}
                </button>
            </div>
        </div>
    )
}

function SharePanel({
    gif,
    onClose,
    trackingQueryString,
}: {
    gif: EmbedGifType
    onClose: () => void
    trackingQueryString?: string
}) {
    const socialUrls = useMemo(() => getSocialUrls(gif), [gif])
    const link = appendTrackingQuery(gif.url, trackingQueryString)
    const embedCode = useMemo(() => getEmbedCode(gif), [gif])

    const openPopup = (url: string, name: string) => {
        window.open(url, name, 'width=575,height=430,menubar=no,toolbar=no,status=no')
    }

    return (
        <div
            className={cn(
                'absolute inset-0 z-40 flex flex-col justify-center bg-black/70 px-4 py-3 text-giphyWhite',
                smallEmbedHiddenClassName
            )}
        >
            <div className="flex gap-1 pb-2">
                <a
                    href={appendTrackingQuery(gif.url, `${trackingQueryString || ''}&ref=fave`.replace(/^&/, ''))}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-9 cursor-pointer items-center justify-center"
                    aria-label="Favorite on GIPHY"
                >
                    <FavoriteIcon
                        gradientId={`embed-favorite-${gif.id}`}
                        className="size-5 [--stop-color-1:var(--giphy-white)] [--stop-color-2:var(--giphy-white)]"
                    />
                </a>
                <button
                    type="button"
                    className="flex size-9 cursor-pointer items-center justify-center"
                    onClick={() => openPopup(socialUrls.facebook, 'facebook')}
                    aria-label="Share on Facebook"
                >
                    <img src="/static/img/soc-icons/facebook@2x.png" alt="" className="size-full" />
                </button>
                <button
                    type="button"
                    className="flex size-9 cursor-pointer items-center justify-center rounded bg-giphyBlack"
                    onClick={() => openPopup(socialUrls.twitter, 'twitter')}
                    aria-label="Share on X"
                >
                    <img src="/static/img/soc-icons/twitter@2x.png" alt="" className="size-5" />
                </button>
                <button
                    type="button"
                    className="flex size-9 cursor-pointer items-center justify-center rounded bg-pinterest"
                    onClick={() => openPopup(socialUrls.pinterest, 'pinterest')}
                    aria-label="Share on Pinterest"
                >
                    <img src="/static/img/mobile/pinterest.svg" alt="" className="size-6" />
                </button>
            </div>
            <div className="flex flex-col gap-3">
                <CopyField label="Embed Code" value={embedCode} copyLabel="Copy Code" />
                <CopyField label="Link" value={link} copyLabel="Copy Link" />
            </div>
            <button
                type="button"
                className="absolute top-2 right-2 flex size-9 cursor-pointer items-center justify-center bg-giphyBlack/80"
                onClick={onClose}
                aria-label="Close share panel"
            >
                <CloseIcon className="size-4" />
            </button>
        </div>
    )
}

export default function EmbedGif({ children, gif, hideVideo = false, isTwitter = false, trackingQueryString }: Props) {
    const [shareOpen, setShareOpen] = useState(false)
    const [paused, setPaused] = useState(false)
    const [mediaOpen, setMediaOpen] = useState(false)
    const [gridBackdropActive, setGridBackdropActive] = useState(false)
    const [continueVisible, setContinueVisible] = useState(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const footerScrollRef = useRef<HTMLDivElement | null>(null)
    const gifVideoUrl = getGifVideoUrl(gif)
    const stillUrl = getGifStillUrl(gif)
    const imageUrl = getGifImageUrl(gif)
    const webpUrl = getGifWebpUrl(gif)
    const mediaUrl = hideVideo ? '' : getExternalMediaUrl(gif.external_media)
    const gifUrl = appendTrackingQuery(gif.url, trackingQueryString)
    const isSticker = !!gif.is_sticker
    const hasRelatedGifs = !!children

    useEffect(() => {
        if (!videoRef.current) {
            return
        }

        if (paused) {
            videoRef.current.pause()
        } else {
            videoRef.current.play().catch(() => undefined)
        }
    }, [paused])

    useEffect(() => {
        if (!shareOpen) {
            return
        }

        footerScrollRef.current?.scrollTo(0, 0)
        setGridBackdropActive(false)
        setContinueVisible(false)
    }, [shareOpen])

    const onFooterScroll = () => {
        const footer = footerScrollRef.current
        if (!footer) {
            return
        }

        const isGridBackdropActive = footer.scrollTop > 0
        const relatedGrid = footer.querySelector<HTMLElement>('[data-related-grid]')
        const isContinueVisible = relatedGrid
            ? relatedGrid.getBoundingClientRect().top <= footer.getBoundingClientRect().top
            : false

        setGridBackdropActive((current) => (current === isGridBackdropActive ? current : isGridBackdropActive))
        setContinueVisible((current) => (current === isContinueVisible ? current : isContinueVisible))
    }

    const resetFooterScroll = () => {
        footerScrollRef.current?.scrollTo(0, 0)
        setGridBackdropActive(false)
        setContinueVisible(false)
    }

    if (mediaOpen && mediaUrl) {
        return (
            <div className="relative h-dvh w-full bg-black text-giphyWhite">
                <iframe title={gif.title || 'GIPHY media'} src={mediaUrl} className="h-full w-full" allow="autoplay" />
                <button
                    type="button"
                    className="absolute right-3 bottom-3 bg-giphyBlack/90 px-3 py-2 text-xs font-black uppercase transition hover:bg-giphyCharcoal"
                    onClick={() => setMediaOpen(false)}
                >
                    GIF
                </button>
            </div>
        )
    }

    return (
        <div
            className={cn(
                'group relative h-dvh w-full overflow-hidden bg-giphyBlack text-giphyWhite',
                'animate-fade-in'
            )}
            onMouseLeave={resetFooterScroll}
        >
            <div
                className={cn(
                    'pointer-events-none absolute inset-0 transition-opacity duration-300',
                    smallEmbedHiddenClassName,
                    isSticker
                        ? 'bg-[#222] bg-[image:linear-gradient(45deg,#161616_25%,transparent_25%,transparent_75%,#161616_75%,#161616),linear-gradient(45deg,#161616_25%,transparent_25%,transparent_75%,#161616_75%,#161616)] bg-[length:60px_60px] bg-[position:0_0,30px_30px]'
                        : 'bg-giphyBlack',
                    shareOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
            />

            <a href={gifUrl} target="_blank" rel="noreferrer" className="absolute inset-0 block" aria-label={gif.title}>
                {gifVideoUrl ? (
                    <video
                        ref={videoRef}
                        src={gifVideoUrl}
                        poster={stillUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="h-full w-full object-contain"
                    >
                        {imageUrl && <img src={imageUrl} alt={gif.alt_text || gif.title || ''} />}
                    </video>
                ) : (
                    <picture>
                        {webpUrl && <source srcSet={webpUrl} type="image/webp" />}
                        <img
                            src={imageUrl || stillUrl}
                            alt={gif.alt_text || gif.title || ''}
                            className="h-dvh w-full object-contain"
                        />
                    </picture>
                )}
                <span
                    className={cn(
                        'pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,.2)_0%,rgba(0,0,0,0)_50%,rgba(0,0,0,.2)_100%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100',
                        smallEmbedHiddenClassName
                    )}
                />
            </a>
            <span
                className={cn(
                    'pointer-events-none absolute inset-0 z-10 bg-giphyBlack transition-opacity duration-300',
                    gridBackdropActive && !shareOpen ? 'opacity-95' : 'opacity-0'
                )}
            />

            <div
                className={cn(
                    'absolute inset-x-0 top-0 z-30 flex items-center justify-between p-3 transition duration-300',
                    smallEmbedHiddenClassName,
                    shareOpen
                        ? 'translate-y-0 opacity-100'
                        : gridBackdropActive
                          ? '-translate-y-full opacity-0'
                          : '-translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                )}
            >
                <a
                    href={appendTrackingQuery('https://giphy.com', trackingQueryString)}
                    target="_blank"
                    rel="noreferrer"
                >
                    <GiphyWordmark className="h-5 w-[72px] md:h-[17px] md:w-[61px]" />
                </a>
                <button
                    type="button"
                    className={cn(iconButtonClassName, 'rounded-sm')}
                    onClick={() => setShareOpen(true)}
                    aria-label="Open share panel"
                >
                    <ShareIcon className="size-4" />
                </button>
            </div>

            <div
                ref={footerScrollRef}
                onScroll={onFooterScroll}
                className={cn(
                    hasRelatedGifs
                        ? 'absolute inset-0 z-20 [scrollbar-width:none] overflow-y-auto overscroll-contain transition duration-300 [&::-webkit-scrollbar]:hidden'
                        : 'absolute inset-x-0 bottom-0 z-20 transition duration-300',
                    smallEmbedHiddenClassName,
                    shareOpen
                        ? 'pointer-events-none translate-y-full opacity-0'
                        : 'translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                )}
            >
                {hasRelatedGifs && (
                    <a
                        href={gifUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute inset-0 z-0 block"
                        aria-label={gif.title}
                    />
                )}
                <div className={cn(hasRelatedGifs && 'pointer-events-none relative z-10 min-h-full pt-[56dvh]')}>
                    <div className={cn('px-3 pt-2', hasRelatedGifs && 'pointer-events-auto')}>
                        <div className="relative flex h-[68px] items-center gap-2 pb-2">
                            {hasRelatedGifs && (
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-black text-giphyWhite">
                                    <span className="border-b border-giphyWhite leading-[29px]">Related GIFs</span>
                                </div>
                            )}
                            {isTwitter && gifVideoUrl && (
                                <button
                                    type="button"
                                    className={iconButtonClassName}
                                    onClick={() => setPaused((value) => !value)}
                                    aria-label={paused ? 'Play GIF' : 'Pause GIF'}
                                >
                                    {paused ? (
                                        <PlayIcon className="size-5" />
                                    ) : (
                                        <span className="text-lg font-black">||</span>
                                    )}
                                </button>
                            )}
                            <GifAttribution gif={gif} trackingQueryString={trackingQueryString} />
                            {mediaUrl && (
                                <button
                                    type="button"
                                    className="bg-giphyIndigo px-3 py-2 text-xs font-black uppercase transition hover:bg-giphyPurple"
                                    onClick={() => setMediaOpen(true)}
                                >
                                    Watch
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={cn(hasRelatedGifs && 'pointer-events-auto')}>{children}</div>
                </div>
            </div>

            {hasRelatedGifs && (
                <div
                    className={cn(
                        'pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center bg-linear-to-b from-transparent via-giphyBlack to-giphyBlack px-4 pt-14 pb-5 transition duration-300',
                        continueVisible && !shareOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                    )}
                >
                    <a
                        data-related-continue
                        href={gifUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="pointer-events-auto flex h-9 w-full max-w-[340px] items-center justify-center bg-giphyIndigo px-4 text-sm font-semibold text-giphyWhite no-underline transition hover:bg-giphyPurple"
                    >
                        Continue Watching on GIPHY
                    </a>
                </div>
            )}

            {shareOpen && (
                <SharePanel gif={gif} onClose={() => setShareOpen(false)} trackingQueryString={trackingQueryString} />
            )}
        </div>
    )
}
